// @ts-nocheck
import React, { useEffect, useState } from "react";
import SharedSelect from "../components/SharedSelect";
import "../styles/Create.css";
import { useNavigate, useParams } from "react-router-dom";
import { deleteListingById, fetchListingById, fetchListings } from "../api/listings";
import { updateListingById } from "../api/listings";
import { startConversation } from "../api/conversations";
import { fetchUserById } from "../api/users";
import { getUser } from "../auth/auth";
import { createOrder } from "../api/orders";
import { createListingReport } from "../api/reports";
import { useToast } from "../components/ToastProvider";

export default function Listing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = getUser();
  const { showToast } = useToast();
  const placeholderImage = "https://via.placeholder.com/900x900/1a1a2e/ffffff?text=Card+Image";

  const [item, setItem] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [messageBusy, setMessageBusy] = useState(false);
  const [similarItems, setSimilarItems] = useState([]);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [seller, setSeller] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ reason: "", description: "" });
  const [shareNotification, setShareNotification] = useState("");
  
  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [shippingName, setShippingName] = useState(user?.username || "");
  const [street, setStreet] = useState("");
  const [apt, setApt] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCVV, setCardCVV] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [processing, setProcessing] = useState(false);

  // Calculate fees for checkout
  const itemPrice = Number(item?.price) || 0;
  const shippingCost = 4.50;
  const buyerProtectionFee = (itemPrice * 0.015).toFixed(2);
  const totalAmount = (itemPrice + shippingCost + parseFloat(buyerProtectionFee)).toFixed(2);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    fetchListingById(id)
      .then((data) => {
        if (!active) return;

        setItem(data);

        // Fetch seller data
        if (data.ownerId) {
          fetchUserById(data.ownerId)
            .then((sellerData) => {
              if (active) setSeller(sellerData);
            })
            .catch(() => {
              // Silently fail for seller data
            });
        }

        // Fetch similar items based on fetched listing data.
        fetchListings()
          .then((listings) => {
            if (!active) return;
            const similar = listings
              .filter((l) => l.game === data.game && l.id !== id)
              .slice(0, 4);
            setSimilarItems(similar);
          })
          .catch(() => {
            // Silently fail for similar items
          });
      })
      .catch(() => {
        if (active) setError("Listing not found");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const handleShare = async () => {
    const listingUrl = `${window.location.origin}/listing/${id}`;
    
    try {
      // Try native share if available
      if (navigator.share) {
        await navigator.share({
          title: item.title,
          text: `Check out this ${item.game} card on Cardex`,
          url: listingUrl,
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(listingUrl);
        setShareNotification("Link copied to clipboard!");
        setTimeout(() => setShareNotification(""), 2000);
      }
    } catch (err) {
      // If share was cancelled or failed, try clipboard
      try {
        await navigator.clipboard.writeText(listingUrl);
        setShareNotification("Link copied to clipboard!");
        setTimeout(() => setShareNotification(""), 2000);
      } catch {
        setShareNotification("Failed to copy link");
        setTimeout(() => setShareNotification(""), 2000);
      }
    }
    setShowShareModal(false);
  };

  const handleReport = async () => {
    if (!reportForm.reason) {
      showToast("Please select a reason", "warning");
      return;
    }
    
    try {
      await createListingReport({
        listingId: id,
        reason: reportForm.reason,
        description: reportForm.description,
      });

      showToast("Thank you for your report. We'll review it shortly.", "success");
      setReportForm({ reason: "", description: "" });
      setShowReportModal(false);
    } catch (err) {
      showToast("Failed to submit report", "error");
    }
  };

  const canManage = item && user && item.ownerId === user.id;

  const handleBuyNow = () => {
    if (!user) {
      showToast("Please log in to purchase", "warning");
      navigate("/login");
      return;
    }
    setShowCheckout(true);
  };

  const fillTestData = () => {
    setShippingName("John Doe");
    setStreet("123 Main Street");
    setApt("Apt 4B");
    setCity("Los Angeles");
    setState("CA");
    setZipCode("90001");
    setCardNumber("4111111111111111");
    setCardName("John Doe");
    setCardExpiry("12/28");
    setCardCVV("123");
  };

  const handlePlaceOrder = async () => {
    setCheckoutError("");
    
    if (!shippingName || !street || !city || !state || !zipCode) {
      setCheckoutError("Please fill in all shipping address fields");
      return;
    }
    
    if (!cardNumber || !cardName || !cardExpiry || !cardCVV) {
      setCheckoutError("Please fill in all payment fields");
      return;
    }
    
    setProcessing(true);
    
    try {
      const orderData = {
        buyerId: user.id,
        buyerName: user.username,
        buyerEmail: user.email || "buyer@example.com",
        sellerId: item.ownerId || "unknown",
        sellerName: seller?.username || "Unknown Seller",
        listingId: item.id,
        itemTitle: item.title,
        itemImage: item.image || item.imageUrl || placeholderImage,
        itemPrice: itemPrice,
        shippingCost: shippingCost,
        buyerProtectionFee: parseFloat(buyerProtectionFee),
        tax: 0.00,
        totalAmount: parseFloat(totalAmount),
        shippingAddress: {
          name: shippingName,
          street: street,
          apt: apt,
          city: city,
          state: state,
          zipCode: zipCode,
          country: "United States"
        },
        paymentMethod: "Credit Card",
        estimatedDelivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: ""
      };
      
      const order = await createOrder(orderData);
      setOrderNumber(order.id);
      setShowCheckout(false);
      setShowConfirmation(true);
    } catch (err) {
      setCheckoutError(err.message || "Failed to create order");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="create-listing-page">
        <div className="create-shell">
          <div className="create-header">
            <button className="back-btn" onClick={() => navigate(-1)}>←</button>
            <div>
              <h1>Loading listing…</h1>
              <p>Please wait a moment.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!item || error) {
    return (
      <div className="create-listing-page">
        <div className="create-shell">
          <div className="create-header">
            <button className="back-btn" onClick={() => navigate(-1)}>←</button>
            <div>
              <h1>Listing not found</h1>
              <p>{error || "That listing may have been deleted."}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-listing-page" style={{ 
      background: 'radial-gradient(1200px 800px at 80% 10%, rgba(88, 101, 242, 0.18), transparent 55%), radial-gradient(900px 700px at 10% 20%, rgba(0, 209, 255, 0.18), transparent 55%), #060914',
      minHeight: '100vh'
    }}>
      <div className="create-shell">
        {/* Header */}
        <div className="create-header">
          <button className="back-btn" onClick={() => navigate(-1)}>←</button>
          <div>
            <h1>{item.title}</h1>
          </div>
        </div>

        {/* Main Content - Two Column Layout */}
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 32, alignItems: "start" }}>
          {/* Left: Image + Actions */}
          <div style={{ display: "grid", gap: 16 }}>
            <div
              className="section-card"
              style={{
                padding: 0,
                overflow: "hidden",
                border: "none",
                background: "transparent",
                position: "relative",
              }}
            >
              <img
                src={activeImage || item.image || placeholderImage}
                alt={item.title}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = placeholderImage;
                }}
                style={{ width: "100%", borderRadius: 14, aspectRatio: "1", objectFit: "cover" }}
              />
              <button
                type="button"
                onClick={() => setIsWishlisted(!isWishlisted)}
                aria-pressed={isWishlisted}
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  background: "rgba(12, 16, 28, 0.7)",
                  color: isWishlisted ? "#ff4da6" : "#eaf1ff",
                  fontSize: 18,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                }}
              >
                {isWishlisted ? "♥" : "♡"}
              </button>
            </div>
            {Array.isArray(item.images) && item.images.length > 1 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {item.images.map((img, idx) => {
                  const isActive = (activeImage || item.image) === img;
                  return (
                    <button
                      key={`${img}-${idx}`}
                      type="button"
                      onClick={() => setActiveImage(img)}
                      style={{
                        width: 56,
                        height: 56,
                        padding: 0,
                        borderRadius: 10,
                        overflow: "hidden",
                        cursor: "pointer",
                        border: isActive ? "2px solid #38BDF8" : "1px solid rgba(255,255,255,0.2)",
                        background: "transparent",
                      }}
                    >
                      <img src={img} alt={`thumbnail ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </button>
                  );
                })}
              </div>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
                <span style={{ color: "#ffffff", opacity: 0.9 }}>Have one to sell?</span>
                <button
                  type="button"
                  className="pill-action"
                  onClick={() => navigate("/create")}
                >
                  Sell one like this
                </button>
                <button
                  type="button"
                  className="pill-action"
                  onClick={() => navigate("/create")}
                >
                  Sell something else
                </button>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="pill-action"
                  onClick={() => setShowShareModal(true)}
                >
                  Share
                </button>
                <button
                  type="button"
                  className="pill-action pill-action--danger"
                  onClick={() => setShowReportModal(true)}
                >
                  Report
                </button>
              </div>
            </div>
          </div>

          {/* Right: Details & Actions */}
          <div style={{ display: "grid", gap: 18 }}>
            {/* Price & Status */}
            <div className="section-card">
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 32, fontWeight: 700, color: "#00ffdd" }}>
                  ${Number(item.price).toFixed(2)}
                </h2>
                {item.status === "sold" && (
                  <span className="progress-step" style={{ backgroundColor: "rgba(255, 0, 0, 0.2)", borderColor: "rgba(255, 0, 0, 0.5)", color: "#ff6b6b" }}>
                    Sold
                  </span>
                )}
              </div>

              {/* Seller Profile Mini Card */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "52px 1fr",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 12px",
                  borderRadius: 12,
                  background: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: seller?.avatar ? `url(${seller.avatar})` : "linear-gradient(135deg, #ff0080, #00ffdd)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    display: "grid",
                    placeItems: "center",
                    color: "#0b0f1a",
                    fontWeight: 700,
                    fontSize: 16,
                  }}
                >
                  {!seller?.avatar && (seller?.name?.[0]?.toUpperCase() || "U")}
                </div>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{seller?.name || item.seller || "Cardex Seller"}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.85 }}>
                    <span style={{ color: "#7cf3ff" }}>★★★★★</span>
                    <span>{seller?.reviewCount || 0} reviews</span>
                    <span style={{ opacity: 0.6 }}>•</span>
                    <span>{seller?.itemsListed || 0} listed</span>
                    <span style={{ opacity: 0.6 }}>•</span>
                    <span>{seller?.salesCount || 0} sales</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.8 }}>
                    {seller?.verified && (
                      <>
                        <span style={{ color: "#6cf2ff" }}>✔</span>
                        <span>Profile verified</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {!canManage && (
              <div style={{ display: "grid", gap: 12 }}>
                {/* Buy Now - Large Neon Button */}
                <button
                  className="btn-neon-large"
                  type="button"
                  onClick={handleBuyNow}
                  style={{
                    padding: "16px 18px",
                    fontSize: 16,
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #00ffdd, #00ff88)",
                    color: "#000",
                    border: "none",
                    borderRadius: 8,
                    cursor: "pointer",
                    boxShadow: "0 0 20px rgba(0, 255, 221, 0.6)",
                    transition: "0.3s",
                  }}
                >
                  Buy Now!
                </button>

                {/* Add to Cart & Wishlist - Side by Side */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {/* Add to Cart */}
                  <button
                    className="btn-secondary-action"
                    type="button"
                    onClick={() => setIsInCart(!isInCart)}
                    style={{
                      padding: "14px 18px",
                      fontSize: 14,
                      fontWeight: 600,
                      background: "#fff",
                      color: "#000",
                      border: "2px solid #fff",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "0.3s",
                    }}
                  >
                    {isInCart ? "Added to Cart" : "Add to Cart"}
                  </button>

                  {/* Add to Wishlist */}
                  <button
                    className="btn-wishlist"
                    type="button"
                    onClick={() => setIsWishlisted(!isWishlisted)}
                    style={{
                      padding: "14px 18px",
                      fontSize: 14,
                      fontWeight: 600,
                      background: "#fff",
                      color: "#000",
                      border: "2px solid #fff",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "0.3s",
                    }}
                  >
                    {isWishlisted ? "Wishlisted" : "Add to Wishlist"}
                  </button>
                </div>

                {/* Message Seller */}
                <button
                  className="btn-message-seller"
                  type="button"
                  disabled={messageBusy}
                  onClick={async () => {
                    if (!item) return;
                    setMessageBusy(true);
                    try {
                      const convo = await startConversation(item.id, "Hi! Is this still available?");
                      navigate(`/messages/${convo.id}`);
                    } catch (err) {
                      showToast(err.message || "Unable to start conversation", "error");
                    } finally {
                      setMessageBusy(false);
                    }
                  }}
                  style={{
                    padding: "12px 18px",
                    fontSize: 13,
                    fontWeight: 600,
                    background: "rgba(80, 120, 220, 0.5)",
                    color: "#fff",
                    border: "2px solid rgba(100, 150, 255, 0.6)",
                    borderRadius: 8,
                    cursor: "pointer",
                    transition: "0.2s",
                  }}
                >
                  {messageBusy ? "Opening…" : "Message Seller"}
                </button>
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  <div style={{ textAlign: "center", color: "#9aa3b2", fontSize: 12 }}>
                    * By continuing to checkout, you agree to the
                    Cardex Privacy Policy and Terms of Service
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr",
                      gap: 10,
                      alignItems: "center",
                      padding: "10px 12px",
                      borderRadius: 10,
                      background: "#ffffff",
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      fontSize: 12,
                      color: "#0b0f1a",
                    }}
                  >
                    <span style={{ fontSize: 16, color: "#0b0f1a" }}>🛒</span>
                    <div>
                      <div style={{ fontWeight: 700, color: "#0b0f1a" }}>1 person has this item in their cart now</div>
                      <div style={{ color: "#0b0f1a" }}>Snag it before it’s gone</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {canManage && (
              <div style={{ display: "grid", gap: 10 }}>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={async () => {
                    const updated = await updateListingById(item.id, {
                      status: item.status === "sold" ? "active" : "sold",
                    });
                    setItem(updated);
                  }}
                  style={{ padding: "12px 18px", fontSize: 14, fontWeight: 600 }}
                >
                  {item.status === "sold" ? "Mark Active" : "Mark Sold"}
                </button>
                <button
                  className="btn-danger"
                  type="button"
                  onClick={async () => {
                    await deleteListingById(item.id);
                    navigate("/marketplace");
                  }}
                  style={{ padding: "12px 18px", fontSize: 14, fontWeight: 600, background: "rgba(255,0,80,0.15)", color: "#ff0050", border: "1px solid rgba(255,0,80,0.3)" }}
                >
                  Delete Listing
                </button>
              </div>
            )}

            {/* Details Section */}
            <div className="section-card">
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.6, marginBottom: 12 }}>
                Details
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr" }}>
                  <span style={{ opacity: 0.6, fontSize: 13 }}>Condition</span>
                  <span style={{ fontSize: 13 }}>
                    <span className="progress-step">{item.condition}</span>
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr" }}>
                  <span style={{ opacity: 0.6, fontSize: 13 }}>Game</span>
                  <span style={{ fontSize: 13 }}>{item.game}</span>
                </div>
                {item.set && (
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr" }}>
                    <span style={{ opacity: 0.6, fontSize: 13 }}>Set</span>
                    <span style={{ fontSize: 13 }}>{item.set}</span>
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr" }}>
                  <span style={{ opacity: 0.6, fontSize: 13 }}>Posted</span>
                  <span style={{ fontSize: 13 }}>{item.time || "Recently"}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr" }}>
                  <span style={{ opacity: 0.6, fontSize: 13 }}>Location</span>
                  <span style={{ fontSize: 13 }}>{item.location?.label || item.locationPostalCode || "Unavailable"}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {item.description && (
              <div className="section-card">
                <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.6, marginBottom: 12 }}>
                  Description
                </div>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, opacity: 0.85 }}>
                  {item.description}
                </p>
              </div>
            )}

            {/* Payment Methods + Shipping/Returns */}
            <div className="section-card">
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.6, marginBottom: 12 }}>
                Payment Methods
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 11, marginBottom: 14 }}>
                <span className="progress-step">Credit Card</span>
                <span className="progress-step">PayPal</span>
                <span className="progress-step">Digital</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="section-card" style={{ margin: 0 }}>
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.6, marginBottom: 10 }}>
                    Shipping
                  </div>
                  <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                    <div>
                      <div style={{ opacity: 0.6, fontSize: 12 }}>From</div>
                      <div style={{ marginTop: 4 }}>{item.shipsFrom || "USA"}</div>
                    </div>
                    <div>
                      <div style={{ opacity: 0.6, fontSize: 12 }}>Handling Time</div>
                      <div style={{ marginTop: 4 }}>{item.handlingTime || "1-2 days"}</div>
                    </div>
                  </div>
                </div>
                <div className="section-card" style={{ margin: 0 }}>
                  <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.3, opacity: 0.6, marginBottom: 10 }}>
                    Returns
                  </div>
                  <div style={{ display: "grid", gap: 8, fontSize: 13 }}>
                    <div>
                      <div style={{ opacity: 0.6, fontSize: 12 }}>Returns Accepted</div>
                      <div style={{ marginTop: 4 }}>{item.returnsAccepted ? "Yes" : "No returns accepted"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Items */}
        {!canManage && similarItems.length > 0 && (
          <div style={{ marginTop: 40 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 18 }}>Similar Items</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 14 }}>
              {similarItems.map((listing) => (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/listing/${listing.id}`)}
                  style={{
                    cursor: "pointer",
                    borderRadius: 10,
                    overflow: "hidden",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    transition: "0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "rgba(0,255,221,0.4)";
                    e.currentTarget.style.boxShadow = "0 0 10px rgba(0,255,221,0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  <img
                    src={listing.image}
                    alt={listing.title}
                    style={{ width: "100%", height: 140, objectFit: "cover" }}
                  />
                  <div style={{ padding: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {listing.title}
                    </div>
                    <div style={{ fontSize: 12, color: "#00ffdd", fontWeight: 700 }}>
                      ${Number(listing.price).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share Notification */}
        {shareNotification && (
          <div style={{
            position: "fixed",
            top: 20,
            right: 20,
            background: "#00ffdd",
            color: "#0b0f1a",
            padding: "12px 20px",
            borderRadius: 8,
            fontWeight: 600,
            zIndex: 10000,
          }}>
            {shareNotification}
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}>
            <div style={{
              background: "#0b0f1a",
              border: "1px solid rgba(0, 255, 221, 0.3)",
              borderRadius: 16,
              padding: 24,
              maxWidth: 400,
              width: "90%",
              boxShadow: "0 0 30px rgba(0, 255, 221, 0.2)",
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 20, fontWeight: 700 }}>Share Listing</h2>
              <p style={{ marginBottom: 20, opacity: 0.8, fontSize: 14 }}>
                {item.title}
              </p>
              
              <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                <button
                  onClick={handleShare}
                  style={{
                    padding: "12px 16px",
                    background: "#00ffdd",
                    color: "#0b0f1a",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  📋 Copy Link
                </button>
                
                {navigator.share && (
                  <button
                    onClick={handleShare}
                    style={{
                      padding: "12px 16px",
                      background: "rgba(0, 255, 221, 0.15)",
                      color: "#00ffdd",
                      border: "1px solid rgba(0, 255, 221, 0.3)",
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    🔗 Share
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowShareModal(false)}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  background: "rgba(255, 255, 255, 0.05)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Report Modal */}
        {showReportModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}>
            <div style={{
              background: "#0b0f1a",
              border: "1px solid rgba(255, 0, 80, 0.3)",
              borderRadius: 16,
              padding: 24,
              maxWidth: 450,
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 0 30px rgba(255, 0, 80, 0.2)",
            }}>
              <h2 style={{ marginTop: 0, marginBottom: 12, fontSize: 20, fontWeight: 700 }}>Report Listing</h2>
              <p style={{ marginBottom: 20, opacity: 0.8, fontSize: 14 }}>
                Help us keep Cardex safe by reporting inappropriate or problematic listings.
              </p>

              <div style={{ display: "grid", gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                    Reason for report *
                  </label>
                  <SharedSelect
                    value={reportForm.reason}
                    onChange={(e) => setReportForm({ ...reportForm, reason: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: 8,
                      color: "#0b0f1a",
                      fontSize: 14,
                      fontFamily: "inherit",
                      cursor: "pointer",
                    }}
                  >
                    <option value="" style={{ color: "#0b0f1a", background: "#ffffff" }}>Select a reason...</option>
                    <option value="counterfeit" style={{ color: "#0b0f1a", background: "#ffffff" }}>Counterfeit or fake item</option>
                    <option value="damaged" style={{ color: "#0b0f1a", background: "#ffffff" }}>Item doesn't match description</option>
                    <option value="prohibited" style={{ color: "#0b0f1a", background: "#ffffff" }}>Prohibited item</option>
                    <option value="spam" style={{ color: "#0b0f1a", background: "#ffffff" }}>Spam or scam</option>
                    <option value="offensive" style={{ color: "#0b0f1a", background: "#ffffff" }}>Offensive content</option>
                    <option value="other">Other</option>
                  </SharedSelect>
                </div>

                <div>
                  <label style={{ display: "block", marginBottom: 8, fontWeight: 600, fontSize: 13 }}>
                    Additional details
                  </label>
                  <textarea
                    value={reportForm.description}
                    onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                    placeholder="Please provide any additional information that would help us review this report..."
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: 8,
                      color: "#ffffff",
                      fontSize: 14,
                      fontFamily: "inherit",
                      minHeight: 100,
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <button
                  onClick={handleReport}
                  style={{
                    padding: "12px 16px",
                    background: "#ff0050",
                    color: "#ffffff",
                    border: "none",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  Submit Report
                </button>

                <button
                  onClick={() => setShowReportModal(false)}
                  style={{
                    padding: "10px 16px",
                    background: "rgba(255, 255, 255, 0.05)",
                    color: "#ffffff",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .btn-neon-large {
          transition: 0.3s !important;
        }
        .btn-neon-large:hover:not(:disabled) {
          box-shadow: 0 0 30px rgba(0, 255, 221, 0.8), 0 0 60px rgba(0, 255, 136, 0.4) !important;
          transform: translateY(-2px) !important;
        }
        .btn-neon-large:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-secondary-action {
          transition: 0.3s !important;
        }
        .btn-secondary-action:hover {
          background: rgba(0, 255, 136, 0.8) !important;
          border-color: rgba(0, 255, 136, 0.9) !important;
          box-shadow: 0 0 15px rgba(0, 255, 136, 0.5) !important;
          color: #000 !important;
        }
        .btn-wishlist {
          transition: 0.3s !important;
        }
        .btn-wishlist:hover {
          background: rgba(255, 0, 128, 0.8) !important;
          border-color: rgba(255, 0, 128, 0.9) !important;
          box-shadow: 0 0 15px rgba(255, 0, 128, 0.5) !important;
          color: #fff !important;
        }
        .btn-message-seller:hover {
          background: rgba(100, 150, 255, 0.7) !important;
          border-color: rgba(100, 200, 255, 0.8) !important;
          box-shadow: 0 0 15px rgba(100, 200, 255, 0.4) !important;
        }
        .btn-danger {
          transition: 0.2s;
          cursor: pointer;
          border-radius: 8px;
          font-weight: 600;
        }
        .btn-danger:hover {
          background: rgba(255,0,80,0.25) !important;
          border-color: rgba(255,0,80,0.6) !important;
        }
        .pill-action {
          padding: 8px 12px;
          border-radius: 999px;
          border: 2px solid #ffffff;
          background: #ffffff;
          color: #0b0f1a;
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
          transition: 0.2s;
          white-space: nowrap;
        }
        .pill-action:hover {
          border-color: rgba(0, 255, 221, 0.9);
          box-shadow: 0 0 14px rgba(0, 255, 221, 0.6), 0 0 28px rgba(0, 255, 221, 0.35);
        }
        .pill-action--danger {
          background: #ffffff;
          color: #0b0f1a;
          border-color: #ffffff;
        }
        .pill-action--danger:hover {
          border-color: rgba(255, 0, 80, 0.9);
          box-shadow: 0 0 14px rgba(255, 0, 80, 0.6), 0 0 28px rgba(255, 0, 80, 0.35);
          background: rgba(255, 0, 80, 0.9);
          color: #ffffff;
        }
        @media (max-width: 900px) {
          .create-shell {
            display: grid;
            grid-template-columns: 1fr;
          }
          .create-listing-page > .create-shell > div:nth-child(3) {
            grid-template-columns: 1fr !important;
          }
        }

        /* Checkout Modal Styles */
        .checkout-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          backdrop-filter: blur(8px);
        }

        .checkout-modal {
          background: #1a1f2e;
          border-radius: 16px;
          width: 95%;
          max-width: 1000px;
          max-height: 90vh;
          overflow-y: auto;
          border: 2px solid rgba(0, 255, 221, 0.3);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }

        .checkout-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px;
          border-bottom: 2px solid rgba(255, 255, 255, 0.1);
        }

        .checkout-header h2 {
          margin: 0;
          font-size: 24px;
          color: #fff;
        }

        .modal-close {
          background: transparent;
          border: none;
          color: #aaa;
          font-size: 28px;
          cursor: pointer;
          transition: 0.2s;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close:hover {
          color: #ff0050;
        }

        .checkout-content {
          display: grid;
          grid-template-columns: 1fr 350px;
          gap: 24px;
          padding: 24px;
        }

        .checkout-main {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .checkout-section {
          background: rgba(255, 255, 255, 0.03);
          padding: 20px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .checkout-section h3 {
          margin: 0 0 16px 0;
          font-size: 18px;
          color: #fff;
        }

        .checkout-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .checkout-input {
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 12px 16px;
          color: #fff;
          font-size: 14px;
          transition: 0.2s;
        }

        .checkout-input:focus {
          outline: none;
          border-color: rgba(0, 255, 221, 0.6);
          box-shadow: 0 0 12px rgba(0, 255, 221, 0.3);
        }

        .checkout-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .checkout-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .checkout-sidebar {
          background: rgba(255, 255, 255, 0.03);
          padding: 20px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          height: fit-content;
          position: sticky;
          top: 24px;
        }

        .checkout-sidebar h3 {
          margin: 0 0 16px 0;
          font-size: 18px;
          color: #fff;
        }

        .order-item {
          display: flex;
          gap: 12px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: 16px;
        }

        .order-item img {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
        }

        .order-item-info {
          flex: 1;
        }

        .order-item-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 4px;
          font-size: 14px;
        }

        .order-item-seller {
          color: rgba(255, 255, 255, 0.5);
          font-size: 12px;
        }

        .order-breakdown {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .order-line {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
        }

        .order-total {
          border-top: 2px solid rgba(255, 255, 255, 0.1);
          padding-top: 12px;
          margin-top: 8px;
          font-weight: 700;
          font-size: 18px;
          color: #fff;
        }

        .checkout-error {
          background: rgba(255, 0, 80, 0.15);
          border: 1px solid rgba(255, 0, 80, 0.4);
          color: #ff0050;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
        }

        .btn-place-order {
          background: linear-gradient(135deg, #00ffdd 0%, #00cc99 100%);
          color: #0b0f1a;
          border: none;
          padding: 16px 32px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: 0.2s;
          box-shadow: 0 4px 16px rgba(0, 255, 221, 0.3);
        }

        .btn-place-order:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(0, 255, 221, 0.5);
        }

        .btn-place-order:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Confirmation Modal Styles */
        .confirmation-modal {
          background: #1a1f2e;
          border-radius: 16px;
          width: 95%;
          max-width: 500px;
          padding: 40px;
          border: 2px solid rgba(0, 255, 221, 0.3);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          text-align: center;
        }

        .confirmation-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #00ffdd 0%, #00cc99 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 48px;
          color: #0b0f1a;
          margin: 0 auto 24px;
          font-weight: bold;
        }

        .confirmation-modal h2 {
          margin: 0 0 8px 0;
          font-size: 28px;
          color: #fff;
        }

        .confirmation-subtitle {
          color: rgba(255, 255, 255, 0.6);
          margin: 0 0 32px 0;
        }

        .confirmation-details {
          background: rgba(255, 255, 255, 0.03);
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .confirmation-line {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.7);
        }

        .confirmation-line:last-child {
          border-bottom: none;
        }

        .confirmation-highlight {
          color: #00ffdd;
          font-weight: 600;
        }

        .confirmation-item {
          display: flex;
          gap: 16px;
          align-items: center;
          background: rgba(255, 255, 255, 0.03);
          padding: 16px;
          border-radius: 12px;
          margin-bottom: 24px;
          text-align: left;
        }

        .confirmation-item img {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
        }

        .confirmation-item-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 4px;
        }

        .confirmation-item-seller {
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
        }

        .confirmation-actions {
          display: flex;
          gap: 12px;
          flex-direction: column;
        }

        .btn-primary {
          background: linear-gradient(135deg, #00ffdd 0%, #00cc99 100%);
          color: #0b0f1a;
          border: none;
          padding: 14px 24px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: 0.2s;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 255, 221, 0.4);
        }

        .btn-secondary {
          background: transparent;
          color: #fff;
          border: 2px solid rgba(255, 255, 255, 0.2);
          padding: 14px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          transition: 0.2s;
        }

        .btn-secondary:hover {
          border-color: rgba(255, 255, 255, 0.4);
          background: rgba(255, 255, 255, 0.05);
        }

        @media (max-width: 900px) {
          .checkout-content {
            grid-template-columns: 1fr;
          }
          
          .checkout-sidebar {
            position: static;
          }
        }
      `}</style>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="checkout-overlay" onClick={() => setShowCheckout(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="checkout-header">
              <h2>Secure Checkout</h2>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button 
                  onClick={fillTestData}
                  style={{
                    background: 'rgba(255, 200, 0, 0.15)',
                    border: '2px solid rgba(255, 200, 0, 0.4)',
                    color: '#ffc800',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: '0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 200, 0, 0.25)';
                    e.currentTarget.style.borderColor = 'rgba(255, 200, 0, 0.6)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 200, 0, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 200, 0, 0.4)';
                  }}
                >
                  Fill Test Data
                </button>
                <button className="modal-close" onClick={() => setShowCheckout(false)}>✕</button>
              </div>
            </div>
            
            <div className="checkout-content">
              <div className="checkout-main">
                {/* Shipping Address */}
                <section className="checkout-section">
                  <h3>Shipping Address</h3>
                  <div className="checkout-form">
                    <input
                      type="text"
                      placeholder="Full name"
                      value={shippingName}
                      onChange={(e) => setShippingName(e.target.value)}
                      className="checkout-input"
                    />
                    <input
                      type="text"
                      placeholder="Street address"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      className="checkout-input"
                    />
                    <input
                      type="text"
                      placeholder="Apt / Unit # (optional)"
                      value={apt}
                      onChange={(e) => setApt(e.target.value)}
                      className="checkout-input"
                    />
                    <div className="checkout-row">
                      <input
                        type="text"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="checkout-input"
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="checkout-input"
                        maxLength="2"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Zip code"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      className="checkout-input"
                      maxLength="5"
                    />
                  </div>
                </section>

                {/* Payment */}
                <section className="checkout-section">
                  <h3>Payment</h3>
                  <div className="checkout-form">
                    <input
                      type="text"
                      placeholder="Card number"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      className="checkout-input"
                      maxLength="16"
                    />
                    <input
                      type="text"
                      placeholder="Name on card"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      className="checkout-input"
                    />
                    <div className="checkout-row">
                      <input
                        type="text"
                        placeholder="MM/YY"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(e.target.value)}
                        className="checkout-input"
                        maxLength="5"
                      />
                      <input
                        type="text"
                        placeholder="CVV"
                        value={cardCVV}
                        onChange={(e) => setCardCVV(e.target.value)}
                        className="checkout-input"
                        maxLength="3"
                      />
                    </div>
                  </div>
                </section>

                {checkoutError && <div className="checkout-error">{checkoutError}</div>}

                <button
                  className="btn-place-order"
                  onClick={handlePlaceOrder}
                  disabled={processing}
                >
                  {processing ? "Processing..." : `Place Order · $${totalAmount}`}
                </button>
              </div>

              {/* Order Summary Sidebar */}
              <div className="checkout-sidebar">
                <h3>Order Summary</h3>
                <div className="order-item">
                  <img src={item.image || item.imageUrl || placeholderImage} alt={item.title} />
                  <div className="order-item-info">
                    <div className="order-item-title">{item.title}</div>
                    <div className="order-item-seller">Sold by @{seller?.username || "Seller"}</div>
                  </div>
                </div>
                
                <div className="order-breakdown">
                  <div className="order-line">
                    <span>Item price</span>
                    <span>${itemPrice.toFixed(2)}</span>
                  </div>
                  <div className="order-line">
                    <span>Shipping</span>
                    <span>${shippingCost.toFixed(2)}</span>
                  </div>
                  <div className="order-line">
                    <span>Buyer protection</span>
                    <span>${buyerProtectionFee}</span>
                  </div>
                  <div className="order-line order-total">
                    <span>Total</span>
                    <span>${totalAmount}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Confirmation Modal */}
      {showConfirmation && (
        <div className="checkout-overlay" onClick={() => setShowConfirmation(false)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-icon">✓</div>
            <h2>Order Confirmed!</h2>
            <p className="confirmation-subtitle">Your order has been placed successfully</p>
            
            <div className="confirmation-details">
              <div className="confirmation-line">
                <span>Order Number</span>
                <span className="confirmation-highlight">{orderNumber}</span>
              </div>
              <div className="confirmation-line">
                <span>Total Amount</span>
                <span className="confirmation-highlight">${totalAmount}</span>
              </div>
              <div className="confirmation-line">
                <span>Estimated Delivery</span>
                <span>{new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="confirmation-item">
              <img src={item.image || item.imageUrl || placeholderImage} alt={item.title} />
              <div>
                <div className="confirmation-item-title">{item.title}</div>
                <div className="confirmation-item-seller">from @{seller?.username || "Seller"}</div>
              </div>
            </div>

            <div className="confirmation-actions">
              <button className="btn-primary" onClick={() => navigate("/orders")}>
                View Orders
              </button>
              <button className="btn-secondary" onClick={() => {
                setShowConfirmation(false);
                navigate("/marketplace");
              }}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
